<template>
  <a-form-item :name="name" :label="label" :required="required">
    <p style="margin-bottom: 8px" v-if="description">{{ description }}</p>

    <a-radio-group class="color_picker__radio__wrapper" :value="color" @update:value="handleChange">
      <template v-if="defaultColors">
        <a-radio
          class="radio__color"
          v-for="defaultColor in defaultColors"
          :style="{ backgroundColor: `${defaultColor}` }"
          :value="defaultColor"
          :key="defaultColor"
        />
      </template>

      <div class="custom-color__selector">
        <p v-if="defaultColors">Personaliza el color:</p>

        <div class="radio-color__container">
          <img :src="brushImage" alt="brush" />

          <a-radio
            class="radio__color"
            :class="{ 'animated-checked': animated }"
            :style="{ backgroundColor: `${customColor}` }"
            :value="customColor"
            @click="openColorPicker"
          />

          <a-input v-model:value="customColor" placeholder="#109FEE" style="margin-left: 14px" />

          <ColorInput ref="colorInputRef" @update:value="handleColorPickerChange" />
        </div>
      </div>
    </a-radio-group>
  </a-form-item>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";

import ColorInput from "@/app/platformSettings/components/input/ColorInput.vue";

import brushImage from "@/app/platformSettings/assets/brush.svg?url";

const props = defineProps<{
  color: string | undefined;
  defaultColors?: string[];
  name: string;
  label: string;
  required: boolean;
  description?: string;
  animated?: boolean;
}>();

const emit = defineEmits<{ "update:color": [color: string] }>();

const handleChange = (newColor: string) => emit("update:color", newColor);

const customColor = ref("#109FEE");
const colorInputRef = ref<InstanceType<typeof ColorInput>>();

const openColorPicker = () => {
  colorInputRef.value?.$el.click();
};

const handleColorPickerChange = (color: string) => {
  customColor.value = color;
  handleChange(color);
};

onMounted(() => {
  if (!props.color) return;

  const colorIsADefaultOne = props.defaultColors?.includes(props.color);

  if (!colorIsADefaultOne) {
    customColor.value = props.color;
  }
});
</script>

<style lang="sass" scoped>
p
  color: $text-secondary
  margin-bottom: 4px
  font-size: 14px

.radio__color
  width: 24px
  height: 24px
  border: 1px solid rgba(0, 0, 0, 0.2)
  margin: 0
  border-radius: 50%
  transition: 0.2s all

.custom-color__selector
  grid-column: 1/-1
  justify-self: start

.radio-color__container
  height: 32px
  display: grid
  grid-template-columns: 24px 32px 1fr
  justify-items: center
  align-items: center
  gap: 4px

.animated-checked.ant-radio-wrapper-checked
  width: 32px
  height: 32px
  transition: 0.2s all

.animated-checked.ant-radio-wrapper-checked::after
  content: "✓"
  position: absolute
  color: $gray-9
  font-size: 18px
  line-height: 0
  width: 100%
  height: 100%
  display: flex
  justify-content: center
  align-items: center
  text-shadow: -0.25px 0.25px 0 white, 0.25px 0.25px 0 white, 0.25px -0.25px 0 white, -0.25px -0.25px 0 white
</style>

<style>
.radio__form__item .ant-form-item-control {
  max-height: 176px !important;
}

.radio__color .ant-radio {
  display: none !important;
}

.color_picker__radio__wrapper.ant-radio-group {
  display: grid !important;
  grid-template-columns: repeat(auto-fit, 32px) !important;
  grid-template-rows: 32px;
  gap: 12px !important;
  justify-items: center !important;
  align-items: center !important;
}

.not-selected {
  width: 24px !important;
  height: 24px !important;
}

.not-selected::after {
  content: "" !important;
}
</style>
